// ──────────────────────────────────────────────────────
// Chat Product-Card Image Hydration
//
// Chat surfaces (mobile /m/q + desktop ChatDrawer) persist
// `productSuggestions` to localStorage so a refresh repaints the
// same product cards. Each `ProductSuggestion` contains a
// snapshot `imageUrl`. When the admin updates `product.imageUrl`
// AFTER an old AI response has been stored, the snapshot still
// points at the stale (or fallback) URL.
//
// We don't want to re-run the AI just to refresh an image. We
// also don't want to mutate the persisted message — name, price,
// reasoning are an immutable historical record. This module
// solves both: it fetches the LATEST `imageUrl` per `productId`
// for every card visible on the page and exposes it as a map.
// The chat renderer overrides only `imageUrl` per card at paint
// time:
//
//     const rendered = mergeProductImage(snapshot, latestImageMap);
//     <CardRenderer product={rendered} ... />
//
// `name`, `price`, `reason`, `brand`, `href`, etc. all flow
// through untouched. Network failures degrade gracefully — the
// snapshot URL is used and the existing `<img onError>` placeholder
// kicks in for broken URLs.
// ──────────────────────────────────────────────────────
import { useEffect, useMemo, useState } from 'react';
import type { ProductSuggestion } from './types';

export interface LatestImageEntry {
    imageUrl: string | null;
    updatedAt: string;
}

export type LatestImageMap = Record<string, LatestImageEntry>;

/**
 * Pure helper — merges a stored ProductSuggestion snapshot with the
 * latest `imageUrl` for that `productId`. The returned object is a
 * SHALLOW COPY: caller passes it to CardRenderer; the original
 * snapshot in `messages` state is never mutated.
 *
 * Override rules:
 *   - latest entry missing                 -> snapshot.imageUrl is kept
 *   - latest.imageUrl is null/empty string -> snapshot.imageUrl is kept
 *     (so a row with no current image doesn't blank a card that had
 *      an older URL — the broken-image onError placeholder stays the
 *      ultimate fallback)
 *   - product has no productId             -> snapshot returned as-is
 *
 * Anything other than `imageUrl` is preserved verbatim.
 */
export function mergeProductImage(
    snapshot: ProductSuggestion,
    latest: LatestImageMap | null | undefined,
): ProductSuggestion {
    if (!latest) return snapshot;
    const id = snapshot.productId;
    if (!id) return snapshot;
    const entry = latest[id];
    if (!entry) return snapshot;
    if (typeof entry.imageUrl !== 'string' || entry.imageUrl.length === 0) return snapshot;
    if (entry.imageUrl === snapshot.imageUrl) return snapshot;
    return { ...snapshot, imageUrl: entry.imageUrl };
}

/**
 * Collect every distinct, non-empty `productId` from any iterable of
 * ProductSuggestions. Used by the chat surfaces to build the input
 * to `useProductImageHydration` from `messages.flatMap(...)`.
 *
 * Pure, dedupes, preserves insertion order (so the call to the
 * backend has a stable, predictable id list — easier to reason
 * about and to unit-test).
 */
export function collectProductIds(suggestions: Iterable<ProductSuggestion> | null | undefined): string[] {
    if (!suggestions) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const s of suggestions) {
        const id = s?.productId;
        if (typeof id !== 'string' || id.length === 0) continue;
        if (seen.has(id)) continue;
        seen.add(id);
        out.push(id);
    }
    return out;
}

/** Backend caps at 50 ids per request — same constant lives on the
 *  controller. We chunk here so a chat with >50 cards still hydrates
 *  the whole page in two calls instead of failing the request. */
export const PRODUCT_IMAGE_BATCH_SIZE = 50;

/**
 * React hook — fetches the latest image map for the given productIds.
 * Returns `{}` until the first response settles. Re-fetches whenever
 * the joined-id key changes (new message, new card, etc.).
 *
 * `endpoint` is injectable so the same hook works for the mobile
 * chat surface (cookie-authenticated proxy) and the desktop kiosk
 * surface (kiosk-keyed proxy) without forcing one to know about the
 * other. Defaults to `/api/products/images` — matches the Next API
 * route that lives next to the existing `/api/products/showcase`.
 *
 * Failure mode: the hook never throws into render. On error or an
 * unparseable response the map stays whatever it was before; the
 * caller falls back to the snapshot via `mergeProductImage`.
 */
export function useProductImageHydration(
    productIds: string[],
    options?: { endpoint?: string },
): LatestImageMap {
    const endpoint = options?.endpoint ?? '/api/products/images';
    // Stable key for the effect — equal arrays should NOT retrigger.
    const idsKey = useMemo(() => productIds.join(','), [productIds]);
    const [map, setMap] = useState<LatestImageMap>({});

    useEffect(() => {
        if (!idsKey) {
            // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset map when product list becomes empty (effect-key change is the trigger; safe synchronous reset).
            setMap({});
            return;
        }
        let cancelled = false;
        const ids = idsKey.split(',').filter(Boolean);
        const batches: string[][] = [];
        for (let i = 0; i < ids.length; i += PRODUCT_IMAGE_BATCH_SIZE) {
            batches.push(ids.slice(i, i + PRODUCT_IMAGE_BATCH_SIZE));
        }

        (async () => {
            const merged: LatestImageMap = {};
            for (const batch of batches) {
                try {
                    const url = `${endpoint}?ids=${encodeURIComponent(batch.join(','))}`;
                    const res = await fetch(url, { cache: 'no-store' });
                    if (!res.ok) continue;
                    const data = (await res.json()) as LatestImageMap;
                    if (data && typeof data === 'object') {
                        Object.assign(merged, data);
                    }
                } catch {
                    // Silent — caller falls back to the snapshot value
                }
            }
            if (!cancelled) setMap(merged);
        })();

        return () => { cancelled = true; };
    }, [idsKey, endpoint]);

    return map;
}
